import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { extractApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Wordmark } from "@/components/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const user = await login(email.trim().toLowerCase(), password);
      // Purchase-intent handoff (mirrors Register.jsx): if the user
      // signed in from a paid CTA they arrived at /login?intent_slug=X;
      // send them back to /plans which will auto-invoke checkout.
      // `location.state.from` (set by RequireAuth-style redirects and
      // PaymentSuccess) still wins over the intent so a returning
      // Stripe roundtrip completes correctly.
      const qs = new URLSearchParams(location.search);
      const intentSlug = qs.get("intent_slug");
      const dest = location.state?.from
        || (intentSlug ? `/plans?intent_slug=${encodeURIComponent(intentSlug)}` : null)
        || (user.role === "admin" ? "/admin" : "/dashboard");
      navigate(dest, { replace: true });
    } catch (err) {
      setError(extractApiError(err, "Login failed."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-base text-fg-primary flex flex-col">
      <a href="#main-content" className="cra3d-skip-link" data-testid="skip-to-main">
        Skip to main content
      </a>
      <header className="border-b border-border-subtle bg-bg-surface">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 min-h-16 py-2 flex flex-wrap items-center justify-between gap-y-2 gap-x-2">
          <Link to="/" className="min-w-0"><Wordmark size="sm" testid="login-header-wordmark" /></Link>
          <Link to="/register" data-testid="login-to-register-link" className="text-sm text-fg-secondary hover:text-fg-primary transition-colors duration-200">Need an account?</Link>
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="flex-1 flex items-start sm:items-center justify-center px-4 py-10 sm:py-16">
        <div className="w-full max-w-md">
          <h1 className="text-3xl sm:text-4xl tracking-tight font-heading font-medium text-fg-primary">Log in</h1>
          <p className="mt-2 text-sm text-fg-secondary">Welcome back.</p>

          {error && (
            <Alert variant="destructive" className="mt-6" data-testid="login-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-bg-surface border-border-subtle text-fg-primary"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-link"
                  className="text-xs text-fg-secondary hover:text-fg-primary transition-colors duration-200"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-bg-surface border-border-subtle text-fg-primary"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting || !email || !password}
              data-testid="login-submit-btn"
              className="w-full bg-accent-warm text-bg-base hover:bg-accent-warm-hover disabled:opacity-50"
            >
              {submitting ? "Signing in…" : "Log in"}
            </Button>
            <p className="text-xs text-fg-secondary text-center">
              New here?{" "}
              <Link to="/register" data-testid="login-register-link" className="text-accent-warm underline underline-offset-2 decoration-accent-warm/40 hover:decoration-accent-warm">
                Create an account
              </Link>
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}
